import {ValidationErrors} from '@angular/forms';
import {PixKeyType} from '@gatewee/types/recipient-bank-account';
import {validate as uuidValidate, version as uuidVersion} from 'uuid';
import {isValidCNPJ, isValidCPF} from '../validators/cpf-cnpjs-validators';
import {formatCpfCnpjDisplay} from './cpf-cnpj-input.helper';

/**
 * Shared PIX key inference / display / validation for bank-account registration
 * and recipient PIX withdrawal (DICT lookup + smart recipient field).
 */
export type PixKeyInferKind =
  | 'unknown'
  | 'email'
  | 'random'
  | 'cpf_partial'
  | 'cpf'
  | 'cnpj'
  | 'phone_partial'
  | 'phone'
  /** Eleven digits that do not pass CPF checksum — user picks document vs phone (ambiguous). */
  | 'cpf_or_phone';

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;

/** Drops a leading Brazil country code (`55`) when the digit string is longer than 11. */
export function stripBrazilCountryCode(digits: string): string {
  if (digits.startsWith('55') && digits.length > 11) {
    return digits.slice(2);
  }
  return digits;
}

/**
 * Prepares PIX keys for API payloads (check-key, create/update bank account).
 * Always trims. Phone/document also strip non-digits (phone → DDD + number).
 * Email and random_key are otherwise left unchanged.
 */
export function sanitizePixKeyForApi(
  pixKey: string,
  pixKeyType: PixKeyType,
): string {
  const trimmed = String(pixKey ?? '').trim();
  if (pixKeyType === PixKeyType.PHONE) {
    return stripBrazilCountryCode(trimmed.replace(/\D/g, ''));
  }
  if (pixKeyType === PixKeyType.DOCUMENT) {
    return trimmed.replace(/\D/g, '');
  }
  return trimmed;
}

/** Heuristic: CPF vs telefone (11 dígitos), CNPJ (14), e-mail, chave EVP (UUID). */
export function inferPixKeyKind(raw: string): PixKeyInferKind {
  const t = raw.trim();
  if (!t) {
    return 'unknown';
  }

  if (t.includes('@')) {
    return 'email';
  }

  const spaceless = t.replace(/\s/g, '');
  if (/[a-zA-Z]/.test(spaceless)) {
    return 'random';
  }

  const digitsOnly = t.replace(/\D/g, '');

  const hexBody = spaceless.replace(/[^0-9a-f]/gi, '');
  const looksLikeUuidToken =
    /^[0-9a-f-]+$/i.test(spaceless) &&
    (/[a-f]/i.test(hexBody) ||
      spaceless.includes('-') ||
      (hexBody.length >= 32 && /^[0-9a-f]+$/i.test(hexBody)));
  if (looksLikeUuidToken && (spaceless.includes('-') || hexBody.length >= 8)) {
    return 'random';
  }

  if (t.includes('+') || /[()]/.test(t)) {
    const n = stripBrazilCountryCode(digitsOnly);
    if (n.length >= 10 && n.length <= 11) {
      return 'phone';
    }
    if (n.length > 0 && n.length < 10) {
      return 'phone_partial';
    }
    return 'unknown';
  }

  if (!digitsOnly) {
    return 'unknown';
  }

  if (digitsOnly.length > 11) {
    return 'cnpj';
  }
  if (digitsOnly.length === 11) {
    return isValidCPF(digitsOnly) ? 'cpf' : 'cpf_or_phone';
  }
  return 'cpf_partial';
}

/** Cosmetic CPF formatting (11 digits). Prefer {@link formatCpfCnpjDisplay} for auto CPF/CNPJ. */
export function formatCpfDigits(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11) {
    return raw.trim();
  }
  return formatCpfCnpjDisplay(raw);
}

/** Cosmetic CNPJ formatting (14 digits). */
export function formatCnpjDigits(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 14) {
    return raw.trim();
  }
  return formatCpfCnpjDisplay(raw);
}

/** Cosmetic phone formatting for DDD + number (10–11 digits). No country-code display. */
export function formatPhoneDigits(raw: string): string {
  const trimmed = raw.trim();
  const d = stripBrazilCountryCode(trimmed.replace(/\D/g, ''));
  if (d.length === 11) {
    return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (d.length === 10) {
    return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return trimmed;
}

export function formatUuidFromHex(raw: string): string {
  const hex = raw.replace(/[^a-fA-F0-9]/g, '').substring(0, 32);
  if (hex.length === 0) {
    return raw;
  }
  const parts = [
    hex.substring(0, 8),
    hex.length > 8 ? hex.substring(8, 12) : '',
    hex.length > 12 ? hex.substring(12, 16) : '',
    hex.length > 16 ? hex.substring(16, 20) : '',
    hex.length > 20 ? hex.substring(20, 32) : '',
  ].filter(Boolean);
  return parts.join('-');
}

/** Format raw EVP hex input into UUID segments only when 32 hex chars are present (call on blur). */
export function formatUuidIfComplete(raw: string): string {
  const hex = raw.replace(/[^a-fA-F0-9]/g, '');
  if (hex.length < 32) {
    return raw;
  }
  return formatUuidFromHex(raw);
}

/** Cosmetic formatting on blur — does not change inferred kind. */
export function formatPixKeyForDisplay(
  raw: string,
  kind: PixKeyInferKind,
  manualKind: 'cpf' | 'phone' | null = null,
): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return trimmed;
  }

  let effectiveKind = kind;
  if (manualKind === 'cpf') {
    effectiveKind = 'cpf';
  } else if (manualKind === 'phone') {
    effectiveKind = 'phone';
  }

  switch (effectiveKind) {
    case 'random':
      return formatUuidIfComplete(raw);
    case 'cpf':
      return formatCpfDigits(raw);
    case 'cnpj':
      return formatCnpjDigits(raw);
    case 'phone':
      return formatPhoneDigits(raw);
    default:
      return trimmed;
  }
}

export function maskForPixKeyKind(kind: PixKeyInferKind): string {
  switch (kind) {
    case 'cpf':
    case 'cpf_partial':
    case 'cpf_or_phone':
      return '000.000.000-00';
    case 'cnpj':
      return '00.000.000/0000-00';
    case 'phone':
    case 'phone_partial':
      return '(00) 00000-0000';
    default:
      return '';
  }
}

/** Maps an inferred kind to the `RecipientBankAccount.pixKeyType` enum used by bank accounts. */
export function mapInferKindToBankAccountPixKeyType(
  kind: PixKeyInferKind,
): PixKeyType | null {
  switch (kind) {
    case 'cpf':
    case 'cnpj':
      return PixKeyType.DOCUMENT;
    case 'phone':
      return PixKeyType.PHONE;
    case 'email':
      return PixKeyType.EMAIL;
    case 'random':
      return PixKeyType.RANDOM_KEY;
    default:
      return null;
  }
}

/** Validate normalized PIX key value for a resolved kind (used by form + verify step). */
export function validatePixKeyByKind(
  raw: string,
  kind: PixKeyInferKind,
): ValidationErrors | null {
  if (!raw.trim()) {
    return null;
  }

  switch (kind) {
    case 'unknown':
      return {pixKeyUnrecognized: true};
    case 'cpf_partial':
    case 'phone_partial':
      return {pixKeyIncomplete: true};
    case 'cpf_or_phone':
      return null;
    case 'cpf': {
      const d = raw.replace(/\D/g, '');
      return isValidCPF(d) ? null : {invalidCPF: true};
    }
    case 'cnpj': {
      const d = raw.replace(/\D/g, '');
      if (d.length < 14) {
        return {pixKeyIncomplete: true};
      }
      return isValidCNPJ(d) ? null : {invalidCNPJ: true};
    }
    case 'phone': {
      const d = stripBrazilCountryCode(raw.replace(/\D/g, ''));
      if (d.length !== 10 && d.length !== 11) {
        return {invalidPhone: true};
      }
      return null;
    }
    case 'email':
      return emailRe.test(raw) ? null : {invalidEmail: true};
    case 'random': {
      const formatted = formatUuidFromHex(raw);
      if (formatted.replace(/[^a-fA-F0-9]/g, '').length < 32) {
        return {pixKeyIncomplete: true};
      }
      return uuidValidate(formatted) && uuidVersion(formatted) === 4
        ? null
        : {invalidUuid: true};
    }
    default:
      return {pixKeyUnrecognized: true};
  }
}
